import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { MessageCircle, Mail, Phone, MapPin, Instagram, Facebook, Star } from 'lucide-react';

const WHATSAPP_URL = 'https://wa.me/message/AIKG7VFJUUNMJ1';
const BOOKING_URL = 'https://book.abbamassage.com/?aff=29';

const SOCIAL_LINKS = {
  instagram: 'https://www.instagram.com/abbamassage',
  facebook: 'https://www.facebook.com/abbamassage',
  google: 'https://maps.app.goo.gl/G2JL5nKRZdTESBVXA',
  tripadvisor: 'https://www.tripadvisor.com.mx/Attraction_Review-g150807-d34151326-Reviews-Abba_Massage_Studio_and_Wellness_Center-Cancun_Yucatan_Peninsula.html'
};

const IMAGES = {
  studio: 'https://customer-assets.emergentagent.com/job_abba-wellness-cancun/artifacts/p81db8rj_SALA%20DE%20ESTAR%20ABBA%20ESTUDIO.JPG',
};

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
};

export const Contact = () => {
  const { t } = useLanguage();

  useEffect(() => {
    const title = 'Contact ABBA Massage in Cancun | Book or WhatsApp';
    const description = 'Contact ABBA Massage in Cancun by WhatsApp, phone or email. Get directions to our downtown massage studio or book an in-home massage.';
    const canonicalUrl = 'https://abbamassage.com/contact';
    const imageUrl = IMAGES.studio;

    document.title = title;

    const setMeta = (attribute, key, value) => {
      let element = document.head.querySelector(`meta[${attribute}="${key}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, key);
        document.head.appendChild(element);
      }
      element.setAttribute('content', value);
    };

    setMeta('name', 'description', description);
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:url', canonicalUrl);
    setMeta('property', 'og:image', imageUrl);
    setMeta('property', 'og:locale', 'en_US');
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image', imageUrl);

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalUrl);
  }, []);

  const contactMethods = [
    {
      icon: MessageCircle,
      label: t('contact.whatsapp'),
      value: 'WhatsApp',
      href: WHATSAPP_URL,
      color: '#25D366',
    },
    {
      icon: Mail,
      label: t('contact.email'),
      value: 'hello@abbamassage.com',
      href: 'mailto:hello@abbamassage.com',
      color: '#A91B60',
    },
    {
      icon: Phone,
      label: t('contact.phone'),
      value: '+52 998 303 2313',
      href: 'tel:+529983032313',
      color: '#C9B99A',
    },
  ];

  const schema = {
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  name: 'Contact ABBA Massage in Cancun',
  url: 'https://abbamassage.com/contact',
  mainEntity: {
    '@type': 'DaySpa',
    '@id': 'https://abbamassage.com/#business',
    name: 'ABBA Massage Studio & Wellness Center',
    url: 'https://abbamassage.com/',
    telephone: '+52 998 303 2313',
    email: 'hello@abbamassage.com',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Calle Playa Brava 26, Manzana 1, Lote 53',
      addressLocality: 'Cancún',
      addressRegion: 'Quintana Roo',
      postalCode: '77508',
      addressCountry: 'MX'
    }
  }
};

  return (
    <div data-testid="contact-page"><script
  type="application/ld+json"
  dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
/>
      {/* Hero Section */}
      <section className="relative pt-32 pb-24 md:pt-40 md:pb-32">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.studio}
            alt="Contact ABBA Massage studio in Cancun"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-2xl"
          >
            <motion.p variants={fadeInUp} className="text-sm uppercase tracking-[0.2em] text-[#C9B99A] mb-4 font-['Manrope'] font-semibold">
              {t('contact.subtitle')}
            </motion.p>
            <motion.h1 variants={fadeInUp} className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl text-white mb-6 tracking-tight">
              {t('contact.title')}
            </motion.h1>
          </motion.div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-24 md:py-32 px-6" data-testid="contact-methods">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
          >
            {contactMethods.map((method) => (
              <motion.a
                key={method.label}
                href={method.href}
                target={method.href.startsWith('http') ? '_blank' : undefined}
                rel={method.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                variants={fadeInUp}
                className="group bg-white rounded-2xl p-8 text-center shadow-xl hover:shadow-2xl transition-all hover:-translate-y-2"
                data-testid={`contact-${method.label.toLowerCase()}`}
              >
                <div 
                  className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center transition-transform group-hover:scale-110"
                  style={{ backgroundColor: `${method.color}15` }}
                >
                  <method.icon className="w-8 h-8" style={{ color: method.color }} />
                </div>
                <h3 className="font-['Playfair_Display'] text-xl text-[#1C1C1C] mb-2">
                  {method.label}
                </h3>
                <p className="text-[#5A5A5A] font-['Manrope']">
                  {method.value}
                </p>
              </motion.a>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Location */}
      <section className="py-24 md:py-32 bg-[#F8F6F3] px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              <motion.div variants={fadeInUp} className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#A91B60] flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-white" />
                </div>
                <h2 className="font-['Playfair_Display'] text-3xl text-[#1C1C1C]">
                  Our Studio
                </h2>
              </motion.div>
              
              <motion.div variants={fadeInUp} className="mb-6">
                <p className="text-lg text-[#1C1C1C] font-['Manrope'] font-medium mb-2">
                  Calle Playa Brava 26, Manzana 1, Lote 53
                </p>
                <p className="text-[#5A5A5A] font-['Manrope']">
                  Cancún, Quintana Roo<br />
                  CP 77508, México
                </p>
              </motion.div>
              
              <motion.p variants={fadeInUp} className="text-lg text-[#5A5A5A] leading-relaxed mb-8 font-['Manrope']">
                Visit us for our full range of services, or book an in-home session and we'll come to you.
              </motion.p>
              
              <motion.div variants={fadeInUp}>
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                  <Button className="bg-[#A91B60] hover:bg-[#8A1650] text-white rounded-full px-8 py-6 font-['Manrope'] font-semibold">
                    {t('cta.bookNow')}
                  </Button>
                </a>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <img
                src={IMAGES.studio}
                alt="ABBA Massage studio location in downtown Cancun"
                className="rounded-2xl shadow-xl w-full aspect-[4/3] object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Social Links */}
      <section className="py-24 md:py-32 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 variants={fadeInUp} className="font-['Playfair_Display'] text-4xl md:text-5xl text-[#1C1C1C] mb-6 tracking-tight">
              Follow our <em className="italic text-[#A91B60]">journey</em>
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-[#5A5A5A] mb-10 font-['Manrope']">
              Stay connected with us on social media for updates, tips, and special offers.
            </motion.p>
            
            <motion.div variants={fadeInUp} className="flex items-center justify-center gap-6">
              <a
                href={SOCIAL_LINKS.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-14 h-14 rounded-full bg-[#F8F6F3] flex items-center justify-center hover:bg-[#C9B99A] transition-colors group"
                data-testid="social-instagram"
              >
                <Instagram className="w-7 h-7 text-[#A91B60] group-hover:text-white transition-colors" />
              </a>
              <a
                href={SOCIAL_LINKS.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="w-14 h-14 rounded-full bg-[#F8F6F3] flex items-center justify-center hover:bg-[#C9B99A] transition-colors group"
                data-testid="social-facebook"
              >
                <Facebook className="w-7 h-7 text-[#A91B60] group-hover:text-white transition-colors" />
              </a>
              <a
                href={SOCIAL_LINKS.google}
                target="_blank"
                rel="noopener noreferrer"
                className="w-14 h-14 rounded-full bg-[#F8F6F3] flex items-center justify-center hover:bg-[#C9B99A] transition-colors group"
                data-testid="social-google"
              >
                <Star className="w-7 h-7 text-[#A91B60] group-hover:text-white transition-colors" />
              </a>
              <a
                href={SOCIAL_LINKS.tripadvisor}
                target="_blank"
                rel="noopener noreferrer"
                className="w-14 h-14 rounded-full bg-[#F8F6F3] flex items-center justify-center hover:bg-[#C9B99A] transition-colors group"
                data-testid="social-tripadvisor"
              >
                <MapPin className="w-7 h-7 text-[#A91B60] group-hover:text-white transition-colors" />
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 md:py-32 bg-[#A91B60]">
        <div className="max-w-3xl mx-auto text-center px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 variants={fadeInUp} className="font-['Playfair_Display'] text-4xl md:text-5xl text-white mb-6 tracking-tight">
              Ready to begin?
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-white/80 mb-10 font-['Manrope']">
              Book your wellness session today.
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#C9B99A] hover:bg-[#C49366] text-white rounded-full px-10 py-6 font-['Manrope'] font-semibold">
                  {t('cta.bookNow')}
                </Button>
              </a>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#25D366] hover:bg-[#128C7E] text-white rounded-full px-10 py-6 font-['Manrope'] font-semibold">
                  <MessageCircle className="mr-2 w-5 h-5" />
                  WhatsApp
                </Button>
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
